/**
 * Button component (shadcn/ui).
 * Primitive UI dùng chung cho admin portal.
 * @AF1.0
 */
import * as React from "react";
import { Slot } from "@radix-ui/react-slot";
import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/lib/utils";

// US-AE.F0-0102 · @UC9098: dùng token Admin Portal (surface/muted/fg/tone-danger) cho MỌI variant.
// Bám bản tham chiếu handoff react_src/.../button.tsx: outline dùng --surface (KHÔNG --bg nền trang),
// hover --muted; destructive dùng tone-danger. Không dùng ui-kit --gp-* (accent/secondary/destructive)
// vì hệ token khác → vỡ ở dark. ring-offset dùng surface để offset khớp bề mặt control (2 theme).
const buttonVariants = cva(
  // `appearance-none`: preflight đặt -webkit-appearance:button → macOS vẽ native button-chrome
  // ĐÈ background-color (nút trông đen/trắng bất kể token). Reset để bg-* của ta thắng (cả 2 theme).
  "appearance-none inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-medium ring-offset-surface transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-primary text-primary-foreground hover:bg-primary-hover",
        // Danger tinted như bản tham chiếu handoff button.tsx (`bg-tone-danger-bg text-tone-danger-fg
        // border-tone-danger-bd`): đọc rõ ở CẢ 2 theme. KHÔNG dùng tone-danger-fg làm nền đặc vì ở dark
        // token này là salmon nhạt (#f17a72) cho CHỮ → nền đặc + chữ trắng chỉ ~2.7:1 (rớt WCAG AA).
        destructive:
          "border border-tone-danger-bd bg-tone-danger-bg text-tone-danger-fg hover:opacity-90",
        outline:
          "border border-border bg-surface text-fg-muted hover:bg-muted hover:text-fg",
        secondary:
          "border border-border bg-muted text-fg hover:bg-muted-2",
        ghost: "text-fg-muted hover:bg-muted hover:text-fg",
        link: "text-primary underline-offset-4 hover:underline",
      },
      size: {
        default: "h-10 px-4 py-2",
        sm: "h-9 rounded-md px-3",
        lg: "h-11 rounded-md px-8",
        icon: "h-10 w-10",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button";
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
